// The account key (D41, docs/07 "The account key"): an ECDSA P-256 key that signs the
// recipient list. The first linked browser generates it extractable exactly once, to
// write the recovery file, then keeps only a non-extractable copy. The public half is a
// 65-byte uncompressed SEC1 point (D27's second algorithm, as WebCrypto exports `raw`).
import { owned } from "../proto/bytes";
import type { SigAlg } from "../proto/frames";
import type { Proto } from "../proto/index";

export const ACCOUNT_ALG: Extract<SigAlg, "ecdsa_p256_sha256"> = "ecdsa_p256_sha256";

const ECDSA = { name: "ECDSA", namedCurve: "P-256" } as const;
const ECDSA_SHA256 = { name: "ECDSA", hash: "SHA-256" } as const;

/** The account key as this browser holds it. */
export interface AccountKey {
  alg: typeof ACCOUNT_ALG;
  /** Non-extractable, `sign` only. */
  privateKey: CryptoKey;
  publicKey: Uint8Array;
}

/** A freshly generated key whose private half can still be exported, once. */
export interface NewAccountKey {
  pkcs8: Uint8Array;
  publicKey: Uint8Array;
}

export async function generateAccountKey(): Promise<NewAccountKey> {
  const pair = await crypto.subtle.generateKey(ECDSA, true, ["sign", "verify"]);
  return {
    pkcs8: new Uint8Array(await crypto.subtle.exportKey("pkcs8", pair.privateKey)),
    publicKey: new Uint8Array(await crypto.subtle.exportKey("raw", pair.publicKey)),
  };
}

/**
 * A key for the platform to hold (D59): generated extractable once, so its 32-byte scalar
 * (JWK `d`) can be sealed to the daemon; the browser keeps no copy.
 */
export async function generateManagedAccountKey(): Promise<{ scalar: Uint8Array; publicKey: Uint8Array }> {
  const pair = await crypto.subtle.generateKey(ECDSA, true, ["sign", "verify"]);
  const jwk = await crypto.subtle.exportKey("jwk", pair.privateKey);
  if (!jwk.d) throw new Error("not an EC private key");
  return {
    scalar: base64UrlToBytes(jwk.d),
    publicKey: new Uint8Array(await crypto.subtle.exportKey("raw", pair.publicKey)),
  };
}

/** Import a PKCS#8 private key for signing only, non-extractable from here on. */
export async function importAccountKey(pkcs8: Uint8Array, publicKey: Uint8Array): Promise<AccountKey> {
  const privateKey = await crypto.subtle.importKey("pkcs8", owned(pkcs8), ECDSA, false, ["sign"]);
  return { alg: ACCOUNT_ALG, privateKey, publicKey };
}

/** The uncompressed SEC1 point of a PKCS#8 key, read through a throwaway extractable import. */
export async function publicKeyOfPkcs8(pkcs8: Uint8Array): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey("pkcs8", owned(pkcs8), ECDSA, true, ["sign"]);
  const jwk = await crypto.subtle.exportKey("jwk", key);
  if (!jwk.x || !jwk.y) throw new Error("not an EC private key");
  const point = new Uint8Array(65);
  point[0] = 0x04;
  point.set(base64UrlToBytes(jwk.x), 1);
  point.set(base64UrlToBytes(jwk.y), 33);
  return point;
}

function base64UrlToBytes(s: string): Uint8Array {
  const b64 = s.replaceAll("-", "+").replaceAll("_", "/");
  const bin = atob(b64 + "=".repeat((4 - (b64.length % 4)) % 4));
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}

export function accountFingerprint(proto: Proto, publicKey: Uint8Array): string {
  return proto.accountKeyFingerprint(ACCOUNT_ALG, publicKey);
}

/** `0123 4567 89ab …`: groups of four, as `ocs account pin` prints it. */
export function groupFingerprint(fingerprint: string): string {
  return fingerprint.match(/.{1,4}/g)?.join(" ") ?? "";
}

/** A P1363 `r ‖ s` signature over `input` (WebCrypto hashes it with SHA-256). */
export async function signWithAccountKey(key: AccountKey, input: Uint8Array): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.sign(ECDSA_SHA256, key.privateKey, owned(input)));
}
